import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, ArrowDownRight, MessageCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { openWhatsApp, scrollToSection } from '../constants';

const HERO_IMAGE =
  'https://images.unsplash.com/photo-1586232880819-5d06b672c942?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200';

const fadeUp = (delay) => ({
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] }
});

const Hero = () => {
  const { t } = useLanguage();

  return (
    <section id="home" data-testid="hero-section" className="relative bg-cream pt-36 md:pt-48 pb-16 md:pb-24 overflow-hidden">
      {/* thin vertical guide lines */}
      <div className="pointer-events-none absolute inset-0 mx-auto max-w-7xl px-6 md:px-12 hidden lg:block">
        <div className="absolute left-12 top-0 bottom-0 w-px bg-ink/5" />
        <div className="absolute right-12 top-0 bottom-0 w-px bg-ink/5" />
      </div>

      <div className="relative mx-auto max-w-7xl px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-10 items-center">
        {/* Left copy */}
        <div className="lg:col-span-7">
          <motion.span {...fadeUp(0)} className="eyebrow text-bordeaux">
            {t.hero.eyebrow}
          </motion.span>

          <motion.h1
            {...fadeUp(0.1)}
            data-testid="hero-title"
            className="mt-7 font-heading text-4xl sm:text-5xl lg:text-6xl xl:text-[4.5rem] leading-[1.05] tracking-tight text-ink"
          >
            {t.hero.titleStart}
            <em className="text-bordeaux font-medium italic">{t.hero.titleAccent}</em>
            {t.hero.titleEnd}
          </motion.h1>

          <motion.p {...fadeUp(0.2)} className="mt-8 max-w-xl text-base md:text-lg leading-relaxed text-ink/65">
            {t.hero.subtitle}
          </motion.p>

          <motion.div {...fadeUp(0.3)} className="mt-10 flex flex-col sm:flex-row gap-4">
            <button
              onClick={openWhatsApp}
              data-testid="hero-cta-whatsapp"
              className="group inline-flex items-center justify-center gap-3 rounded-full bg-gold hover:bg-gold-dark active:scale-95 text-ink font-bold px-8 py-4"
            >
              <MessageCircle className="w-5 h-5" strokeWidth={2} />
              {t.hero.cta1}
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </button>
            <button
              onClick={() => scrollToSection('services')}
              data-testid="hero-cta-services"
              className="inline-flex items-center justify-center gap-3 rounded-full border border-bordeaux text-bordeaux hover:bg-bordeaux hover:text-cream active:scale-95 font-semibold px-8 py-4"
            >
              {t.hero.cta2}
              <ArrowDownRight className="w-4 h-4" />
            </button>
          </motion.div>

          <motion.ul
            {...fadeUp(0.45)}
            data-testid="hero-trust-list"
            className="mt-14 grid grid-cols-1 sm:grid-cols-3 border-t border-ink/10"
          >
            {t.hero.trust.map((item, i) => (
              <li
                key={item}
                className={`flex items-center gap-3 py-5 text-sm font-medium text-ink/70 ${
                  i > 0 ? 'sm:pl-6 sm:border-l sm:border-ink/10' : ''
                }`}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-bordeaux" />
                {item}
              </li>
            ))}
          </motion.ul>
        </div>

        {/* Right visual */}
        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-5 relative"
          data-testid="hero-visual"
        >
          <div className="relative aspect-[4/5] max-w-md mx-auto lg:max-w-none">
            {/* gold offset frame */}
            <div className="absolute top-4 left-4 -bottom-4 -right-4 md:top-6 md:left-6 md:-bottom-6 md:-right-6 border border-gold" />
            {/* image */}
            <div className="relative h-full w-full overflow-hidden">
              <img src={HERO_IMAGE} alt="Client BiTExpress" className="h-full w-full object-cover" />
              <div className="absolute inset-0 bg-bordeaux/25 mix-blend-multiply" />
            </div>
            {/* floating card */}
            <div className="absolute -left-4 md:-left-10 bottom-10 bg-bordeaux text-cream px-6 py-5 shadow-[0_16px_40px_rgba(26,10,13,0.25)]">
              <p className="text-[10px] uppercase tracking-[0.2em] text-gold font-bold">{t.hero.cardLabel}</p>
              <p className="mt-2 font-heading text-2xl">{t.hero.cardValue}</p>
              <span className="absolute top-4 right-4 w-2 h-2 rounded-full bg-gold animate-pulse" />
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
